import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Loader2, RefreshCw, UsersRound } from "lucide-react";
import { api } from "../../lib/axios";

type AgentListItem = {
  id: number;
  nom: string;
  prenom: string;
  actif: boolean;
  equipeId: number | null;
  equipe?: { nom: string } | null;
};

type ZoneLite = { id: number; nom: string; code?: string | null };

type UserChefOption = {
  id: number;
  nom: string;
  prenom: string | null;
  actif: boolean;
};

type EquipeDetailPayload = {
  equipe: {
    id: number;
    nom: string;
    actif: boolean;
    capaciteJour: number;
    zone: ZoneLite | null;
    chef: { id: number; nom: string; prenom: string | null } | null;
    agents: {
      id: number;
      nom: string;
      prenom: string;
      fonction: string | null;
      telephone1: string;
      actif: boolean;
    }[];
    tournees: {
      id: number;
      numero: string | null;
      statut: string;
      date: string;
      passages: { id: number; statut: string }[];
    }[];
  };
  chargeDuJour: {
    tourneesCount: number;
    passagesCount: number;
    capaciteJour: number;
    degraded: boolean;
    note?: string;
  };
};

export default function EquipeDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<EquipeDetailPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [allAgents, setAllAgents] = useState<AgentListItem[]>([]);
  const [pickAgents, setPickAgents] = useState<number[]>([]);
  const [zones, setZones] = useState<ZoneLite[]>([]);
  const [chefUsers, setChefUsers] = useState<UserChefOption[]>([]);
  const [metaNom, setMetaNom] = useState("");
  const [metaZoneId, setMetaZoneId] = useState<number | "">("");
  const [metaCapacite, setMetaCapacite] = useState<number | "">("");
  const [metaActif, setMetaActif] = useState(true);
  const [metaChefId, setMetaChefId] = useState<number | "">("");
  const [savingMeta, setSavingMeta] = useState(false);

  const load = useCallback(async () => {
    const nid = Number(id);
    if (Number.isNaN(nid)) {
      toast.error("Identifiant invalide.");
      return;
    }
    setLoading(true);
    try {
      const [res, agentsRes, zonesRes, usersRes] = await Promise.all([
        api.get(`/equipes/${nid}`),
        api.get("/agents"),
        api.get("/zones"),
        api.get("/users").catch(() => ({ data: [] as UserChefOption[] })),
      ]);
      const payload = res.data as EquipeDetailPayload;
      setData(payload);
      const eq = payload.equipe;
      setMetaNom(eq.nom);
      setMetaZoneId(eq.zone?.id ?? "");
      setMetaCapacite(eq.capaciteJour);
      setMetaActif(eq.actif);
      setMetaChefId(eq.chef?.id ?? "");
      const rawZ = Array.isArray(zonesRes.data) ? zonesRes.data : zonesRes.data?.data ?? [];
      setZones(
        (rawZ as ZoneLite[]).map((z) => ({
          id: z.id,
          nom: z.nom,
          code: z.code,
        }))
      );
      const rawUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
      setChefUsers(
        rawUsers.map((u: UserChefOption) => ({
          id: u.id,
          nom: u.nom,
          prenom: u.prenom,
          actif: u.actif,
        }))
      );
      const raw = Array.isArray(agentsRes.data) ? agentsRes.data : [];
      setAllAgents(
        raw.map((a: AgentListItem & { equipe?: { nom: string } }) => ({
          id: a.id,
          nom: a.nom,
          prenom: a.prenom,
          actif: a.actif,
          equipeId: a.equipeId ?? null,
          equipe: a.equipe ?? null,
        }))
      );
      const memberIds = new Set((payload.equipe.agents ?? []).map((a) => a.id));
      setPickAgents([...memberIds]);
    } catch {
      toast.error("Impossible de charger l’équipe.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const chefSelectOptions = useMemo(() => {
    const ch = data?.equipe.chef;
    const map = new Map<number, UserChefOption>();
    for (const u of chefUsers) map.set(u.id, u);
    if (ch && !map.has(ch.id)) {
      map.set(ch.id, {
        id: ch.id,
        nom: ch.nom,
        prenom: ch.prenom,
        actif: true,
      });
    }
    const mid = metaChefId === "" ? null : metaChefId;
    return [...map.values()]
      .filter((u) => u.actif || (mid != null && u.id === mid))
      .sort((a, b) => a.nom.localeCompare(b.nom));
  }, [chefUsers, data?.equipe.chef, metaChefId]);

  const saveMeta = async () => {
    const nid = Number(id);
    if (Number.isNaN(nid)) return;
    const nom = metaNom.trim();
    if (nom.length < 2) {
      toast.error("Le nom doit contenir au moins 2 caractères.");
      return;
    }
    setSavingMeta(true);
    try {
      await api.put(`/equipes/${nid}`, {
        nom,
        zoneId: metaZoneId === "" ? null : Number(metaZoneId),
        capaciteJour:
          metaCapacite === "" ? null : Math.max(0, Math.floor(Number(metaCapacite))),
        actif: metaActif,
        chefId: metaChefId === "" ? null : Number(metaChefId),
      });
      toast.success("Informations enregistrées.");
      void load();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error || "Enregistrement impossible.");
    } finally {
      setSavingMeta(false);
    }
  };

  const saveAgents = async () => {
    const nid = Number(id);
    if (Number.isNaN(nid)) return;
    try {
      await api.patch(`/equipes/${nid}/agents`, { agentIds: pickAgents });
      toast.success("Membres mis à jour.");
      void load();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error || "Mise à jour impossible.");
    }
  };

  if (loading || !data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const { equipe, chargeDuJour } = data;
  const overCapacity =
    chargeDuJour.capaciteJour > 0 &&
    chargeDuJour.passagesCount > chargeDuJour.capaciteJour;

  return (
    <div className="space-y-6 p-4 text-slate-100 md:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <Link
          to="/admin/organisation-terrain"
          className="text-sm text-emerald-400/90 underline-offset-2 hover:underline"
        >
          Organisation terrain
        </Link>
      </div>

      <header className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400/90">
              Équipe terrain
            </p>
            <h1 className="mt-1 text-2xl font-bold text-white md:text-3xl">{equipe.nom}</h1>
            <p className="mt-2 text-sm text-slate-400">
              Zone :{" "}
              {equipe.zone ? (
                <Link className="text-emerald-400 underline" to="/admin/organisation-terrain">
                  {equipe.zone.nom}
                </Link>
              ) : (
                "—"
              )}
              {" · "}
              Chef :{" "}
              {equipe.chef
                ? `${equipe.chef.prenom ?? ""} ${equipe.chef.nom}`.trim()
                : "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
          >
            <RefreshCw className="h-4 w-4" /> Actualiser
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-700 bg-slate-950/40 px-4 py-3">
            <p className="text-xs text-slate-500">Capacité jour</p>
            <p className="text-xl font-semibold text-white">{equipe.capaciteJour}</p>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-950/40 px-4 py-3">
            <p className="text-xs text-slate-500">Passages du jour</p>
            <p className="text-xl font-semibold text-white">{chargeDuJour.passagesCount}</p>
            {overCapacity && (
              <p className="mt-1 text-xs font-medium text-amber-400">
                Capacité dépassée ({chargeDuJour.passagesCount} / {chargeDuJour.capaciteJour})
              </p>
            )}
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-950/40 px-4 py-3">
            <p className="text-xs text-slate-500">Tournées du jour</p>
            <p className="text-xl font-semibold text-white">{chargeDuJour.tourneesCount}</p>
            {chargeDuJour.degraded && chargeDuJour.note && (
              <p className="mt-1 text-xs text-slate-500">{chargeDuJour.note}</p>
            )}
          </div>
        </div>
      </header>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="text-lg font-semibold text-white">Modifier l&apos;équipe</h2>
        <p className="mt-1 text-xs text-slate-500">
          Nom, zone, capacité quotidienne, statut et chef d&apos;équipe.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-slate-400">Nom</span>
            <input
              type="text"
              value={metaNom}
              onChange={(e) => setMetaNom(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-white"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-400">Zone</span>
            <select
              value={metaZoneId === "" ? "" : String(metaZoneId)}
              onChange={(e) =>
                setMetaZoneId(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-white"
            >
              <option value="">— Aucune —</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-slate-400">Capacité jour (passages)</span>
            <input
              type="number"
              min={0}
              value={metaCapacite === "" ? "" : metaCapacite}
              onChange={(e) => {
                const v = e.target.value;
                setMetaCapacite(v === "" ? "" : Math.max(0, parseInt(v, 10) || 0));
              }}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-white"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-400">Chef d&apos;équipe</span>
            <select
              value={metaChefId === "" ? "" : String(metaChefId)}
              onChange={(e) =>
                setMetaChefId(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-white"
            >
              <option value="">— Aucun —</option>
              {chefSelectOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.prenom ?? ""} {u.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={metaActif}
              onChange={(e) => setMetaActif(e.target.checked)}
              className="rounded border-slate-600"
            />
            <span className="text-slate-300">Équipe active</span>
          </label>
        </div>
        <button
          type="button"
          disabled={savingMeta}
          onClick={() => void saveMeta()}
          className="mt-4 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
        >
          {savingMeta ? "Enregistrement…" : "Enregistrer les informations"}
        </button>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="text-lg font-semibold text-white">Agents</h2>
        <ul className="mt-3 divide-y divide-slate-800">
          {equipe.agents.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <span>
                {a.prenom} {a.nom}{" "}
                <span className="text-slate-500">
                  · {a.fonction ?? "—"} · {a.telephone1}
                </span>
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  a.actif ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-600 text-slate-300"
                }`}
              >
                {a.actif ? "Actif" : "Inactif"}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
          <UsersRound className="h-5 w-5 text-emerald-400" />
          Affecter / retirer des agents
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Cochez les agents à rattacher à cette équipe (agents sans équipe ou déjà membres).
        </p>
        <div className="mt-4 max-h-56 overflow-y-auto rounded-xl border border-slate-700 bg-slate-950/40 p-3 space-y-2">
          {allAgents
            .filter((a) => a.actif)
            .sort((a, b) => a.nom.localeCompare(b.nom))
            .map((a) => (
              <label key={a.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={pickAgents.includes(a.id)}
                  onChange={(e) => {
                    setPickAgents((prev) =>
                      e.target.checked ? [...prev, a.id] : prev.filter((x) => x !== a.id)
                    );
                  }}
                  className="rounded border-slate-600"
                />
                <span>
                  {a.prenom} {a.nom}
                  {a.equipeId != null && a.equipeId !== Number(id) && (
                    <span className="text-slate-500">
                      {" "}
                      (équipe : {a.equipe?.nom ?? `#${a.equipeId}`})
                    </span>
                  )}
                </span>
              </label>
            ))}
        </div>
        <button
          type="button"
          onClick={() => void saveAgents()}
          className="mt-4 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
        >
          Enregistrer les membres
        </button>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="text-lg font-semibold text-white">Tournées du jour</h2>
        {equipe.tournees.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Aucune tournée pour aujourd’hui.</p>
        ) : (
          <ul className="mt-3 space-y-2 text-sm">
            {equipe.tournees.map((t) => (
              <li
                key={t.id}
                className="flex flex-wrap justify-between gap-2 rounded-lg border border-slate-700 px-3 py-2"
              >
                <span>
                  {t.numero ?? `#${t.id}`} · {t.statut}
                </span>
                <span className="text-slate-400">
                  {t.passages.length} passage(s)
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
